import { DRAW_MODAL } from 'src/reducers/modal/types';
import { LOAD_PRACTICE, LOADED_PENDING_PRACTICES } from 'src/reducers/practice/types';
import {
  ACCEPTED_PRACTICE_PROGRAM,
  LINK_PRACTICE,
  LOADED_CONNECTED_PRACTICES,
  UNLINK_PRACTICE,
} from 'src/reducers/user/types';

import { urls } from 'Utilities/dataHelpers';
import * as log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { observeStore, store } from 'Utilities/store';

import {
  DECLINE_ETAPES_PRACTICE_INVITE_MODAL,
  DECLINE_PRACTICE_INVITE_MODAL,
  UNLINK_ETAPES_PRACTICE_MODAL,
  UNLINK_PRACTICE_MODAL,
} from 'Constants/modalTypes';

const handleGetById = (request: any) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<Practice>('api:invoke', {
        method: 'GET',
        url: urls.lslApi + 'sharing/practice/' + request.practiceId,
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({ type: LOAD_PRACTICE, practice: result.data });
          resolve(result);
        } else {
          mediator.publish('error:display', {
            error: {
              code: result.status,
              type: 'ERROR_GET_PRACTICE_DETAILS',
            },
          });
          reject(result);
        }
      })
      .catch((err) => {
        log.error(err);
        mediator.publish('error:display', {
          error: {
            code: err.status,
            type: 'ERROR_GET_PRACTICE_DETAILS',
          },
        });
        reject(err);
      });
  });
};

const handleGetByPracticeId = (request: any) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request<Practice>('api:invoke', {
        method: 'GET',
        url: urls.lslApi + 'sharing/practice/b/' + request.practiceId,
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({ type: LOAD_PRACTICE, practice: result.data });
          resolve(result);
        } else if ([1, 2].includes(result.status)) {
          resolve(result);
        } else {
          mediator.publish('error:display', {
            error: {
              code: result.status,
              type: 'ERROR_GET_PRACTICE_DETAILS',
            },
          });
          reject(result);
        }
      })
      .catch((err) => {
        log.error(err);
        mediator.publish('error:display', {
          error: {
            code: err.status,
            type: 'ERROR_GET_PRACTICE_DETAILS',
          },
        });
        reject(err);
      });
  });
};

const handleGetConnectedPractices = (request: any) => {
  return (request.response = new Promise<PracticeMap>((resolve, reject) => {
    mediator
      .request<PracticeMap>('api:invoke', {
        method: 'GET',
        url: urls.lslApi + 'sharing/practice/connected',
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({ type: LOADED_CONNECTED_PRACTICES, practices: result.data });
          resolve(result.data);
        } else {
          mediator.publish('error:display', {
            error: {
              code: result.status,
              type: 'ERROR_LOAD_CONNECTED_PRACTICES',
            },
          });
          reject(result);
        }
      })
      .catch((err) => {
        mediator.publish('error:display', {
          error: {
            code: err.status,
            type: 'ERROR_LOAD_CONNECTED_PRACTICES',
          },
        });
        reject(err);
      });
  }));
};

const handleGetPendingPractices = (request: any) => {
  return (request.response = new Promise<PracticeMap>((resolve, reject) => {
    mediator
      .request<{ invitations: Invitation[]; practices: PracticeMap }>('api:invoke', {
        method: 'GET',
        url: urls.lslApi + 'sharing/practice/pending',
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({
            type: LOADED_PENDING_PRACTICES,
            pending: result.data.practices,
            invitations: result.data.invitations,
          });
          resolve(result.data.practices);
        } else {
          mediator.publish('error:display', {
            error: {
              code: result.status,
              type: 'ERROR_LOAD_PENDING_PRACTICES',
            },
          });
          reject(result);
        }
      })
      .catch((err) => {
        mediator.publish('error:display', {
          error: {
            code: err.status,
            type: 'ERROR_LOAD_PENDING_PRACTICES',
          },
        });
        reject(err);
      });
  }));
};

const handleInviteAccept = (request: any) => {
  const {
    practice: { invitations },
  } = store.getState();

  request.response = new Promise((resolve, reject) => {
    mediator
      .request<PracticeMap>('api:invoke', {
        method: 'POST',
        url: urls.lslApi + 'invitations/' + request.inviteId,
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({ type: LOADED_CONNECTED_PRACTICES, practices: result.data });

          if (invitations.length > 0) {
            const foundInv = invitations.find((i) => i.id === request.inviteId);

            // If this invivitation is for a program, we update our user state to reflect that
            if (foundInv && foundInv.custom && foundInv.custom.programId) {
              store.dispatch({
                type: ACCEPTED_PRACTICE_PROGRAM,
                practice: result.data[foundInv.entityId],
                programId: foundInv.custom.programId,
              });
            }
          }

          resolve(result);
        } else if (result.status === 3) {
          resolve(result);
        } else {
          mediator.publish('error:display', {
            error: {
              code: result.status,
              type: 'ERROR_INVITE_ACCEPT',
            },
          });
          reject();
        }
      })
      .catch((err) => {
        mediator.publish('error:display', {
          error: {
            code: err.status,
            type: 'ERROR_INVITE_ACCEPT',
          },
        });
        reject(err);
      });
  });
};

const handleInviteDecline = (request: any) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request('api:invoke', {
        method: 'DELETE',
        url: urls.lslApi + 'sharing/invitations/' + request.inviteId,
      })
      .then((result) => {
        if (result.status === 0) {
          resolve(result);
        } else {
          mediator.publish('error:display', {
            error: {
              code: result.status,
              type: 'ERROR_INVITE_DECLINE',
            },
          });
          reject(result);
        }
      })
      .catch((err) => {
        mediator.publish('error:display', {
          error: {
            code: err.status,
            type: 'ERROR_INVITE_DECLINE',
          },
        });
        reject(err);
      });
  });
};

const handleInviteDeclineModal = (request: any) => {
  const {
    practice: { invitations, pending },
  } = store.getState();

  if (invitations.length > 0) {
    const foundInv = invitations.find((i) => i.id === request.inviteId);

    // Display the Etapes decline modal
    if (foundInv && foundInv.custom && foundInv.custom.programId === 'etapes') {
      const practice = Object.values(pending).find(
        (p) => p.invitationId && p.invitationId === foundInv.id
      );

      if (practice) {
        store.dispatch({
          type: DRAW_MODAL,
          modal: DECLINE_ETAPES_PRACTICE_INVITE_MODAL,
          props: {
            callback: request.callback,
            inviteId: request.inviteId,
            practiceName: practice.name,
          },
        });

        return;
      }
    }
  }

  // Otherwise the normal decline modal
  store.dispatch({
    type: DRAW_MODAL,
    modal: DECLINE_PRACTICE_INVITE_MODAL,
    props: { callback: request.callback, inviteId: request.inviteId },
  });
};

const handleLinkPractice = (request: any) => {
  request.response = new Promise<LVApiResponse<Practice>>((resolve, reject) => {
    mediator
      .request<Practice>('api:invoke', {
        method: 'POST',
        url: urls.lslApi + 'sharing/practice/link',
        data: {
          practiceId: request.practiceId,
        },
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({ type: LINK_PRACTICE, practice: result.data });
          resolve(result);
        } else if (result.status === 1 || result.status === 2) {
          mediator.publish('error:display', {
            error: {
              code: result.status,
              type: 'ERROR_LINK_PRACTICE',
            },
          });
          reject(result);
        } else if (result.status === 3) {
          resolve(result);
        } else {
          mediator.publish('error:display', {
            error: {
              code: result.status,
              type: 'ERROR_LINK_PRACTICE',
            },
          });
          reject(result);
        }
      })
      .catch((err) => {
        mediator.publish('error:display', {
          error: {
            code: err.status,
            type: 'ERROR_LINK_PRACTICE',
          },
        });
        log.error(err);
        reject(err);
      });
  });
};

const handleStopSharing = (request: any) => {
  request.response = new Promise((resolve, reject) => {
    mediator
      .request('api:invoke', {
        method: 'DELETE',
        url: urls.lslApi + 'sharing/practice/' + request.practiceId,
      })
      .then((result) => {
        if (result.status === 0) {
          store.dispatch({ type: UNLINK_PRACTICE, practiceId: request.practiceId });
          resolve(result);
        } else {
          mediator.publish('error:display', {
            error: {
              code: result.status,
              type: 'ERROR_UNLINK_PRACTICE',
            },
          });
          reject(result);
        }
      })
      .catch((err) => {
        mediator.publish('error:display', {
          error: {
            code: err.status,
            type: 'ERROR_UNLINK_PRACTICE',
          },
        });
        reject(err);
      });
  });
};

const handleStopSharingModal = (request: any) => {
  if (!request.practiceId) {
    return;
  }

  const { practices, programs } = store.getState().user;
  const found = practices[request.practiceId];

  if (found) {
    const program = Object.values(programs).find((p) => p.enrolledPractice === found.id);

    // Display the Etapes unlink modal
    if (program && program.enrolled && program.id === 'etapes') {
      store.dispatch({
        type: DRAW_MODAL,
        modal: UNLINK_ETAPES_PRACTICE_MODAL,
        props: {
          callback: request.callback,
          practiceId: request.practiceId,
          practiceName: found.name,
        },
      });

      return;
    }
  }

  // Otherwise the normal unlink modal
  store.dispatch({
    type: DRAW_MODAL,
    modal: UNLINK_PRACTICE_MODAL,
    props: { callback: request.callback, practiceId: request.practiceId },
  });
};

const handleLibreViewRouting = (event: any) => {
  const parts = event.canonicalPath.split('/');

  if (parts.length === 2) {
    Promise.all([handleGetConnectedPractices({}), handleGetPendingPractices({})]).catch((e) => {
      log.error(e);
    });
  }
};

mediator.subscribe('practice:getById', handleGetById);
mediator.subscribe('practice:getByPracticeId', handleGetByPracticeId);
mediator.subscribe('practice:getConnectedPractices', handleGetConnectedPractices);
mediator.subscribe('practice:getPendingPractices', handleGetPendingPractices);
mediator.subscribe('practice:inviteAccept', handleInviteAccept);
mediator.subscribe('practice:inviteDecline', handleInviteDecline);
mediator.subscribe('practice:inviteDeclineModal', handleInviteDeclineModal);
mediator.subscribe('practice:link', handleLinkPractice);
mediator.subscribe('practice:stopSharing', handleStopSharing);
mediator.subscribe('practice:stopSharingModal', handleStopSharingModal);

mediator.subscribe('page:libreview', handleLibreViewRouting);

mediator.once('store:created', () => {
  observeStore(
    (state) => state.user.id,
    (userId) => {
      if (userId) {
        Promise.all([handleGetConnectedPractices({}), handleGetPendingPractices({})]);
      }
    }
  );
});
